/* eslint-disable react/no-danger */
import { useCallback, useState } from "react";
import { Box, Text, Select } from "@mantine/core";
import {
  useAccounts,
  useSurgeryCenters,
  usePhysicians,
} from "../../hooks/useReferenceData";
import { useUpdateCaseRequisition } from "../../hooks/useWorkflows";
import { resolveEffectiveClientReportIdentity } from "../../utils/reportIdentity";
import type { DocumentHeaderProps } from "./DocumentHeaderTypes";
import {
  headerContactLineStyle,
  headerPracticeNameStyle,
  patientRowStyle,
  reportTitleBarStyle,
  reportTitleTextStyle,
} from "./DocumentHeaderStyles";
import { formatDate } from "./documentHeaderFormatting";
import { DateEditor } from "./DateEditor";
import { EditableInfoRow } from "./EditableInfoRow";
import { InfoRow } from "./InfoRow";
import { InlineFieldPair } from "./InlineFieldPair";
import { InlineSelect } from "./InlineSelect";
import { MrnEditor } from "./MrnEditor";
import { PatientNameEditor } from "./PatientNameEditor";
import { ReportLaboratoryLogo } from "./ReportLaboratoryLogo";

export { DocumentFooter } from "./DocumentFooter";

export function DocumentHeader({
  title,
  facility,
  practice,
  patient,
  caseInfo,
  caseId,
  isReadOnly = false,
  onSaved,
  facilityLogoUrl,
  practiceLogoUrl,
  reportIdentity,
  isContinuationPage = false,
}: DocumentHeaderProps) {
  const updateRequisition = useUpdateCaseRequisition();
  const editQueryOptions = { enabled: !isReadOnly };
  const { data: practicesRaw = [] } = useAccounts(editQueryOptions);
  const { data: physiciansRaw = [] } = usePhysicians(editQueryOptions);
  const { data: surgeryCentersRaw = [] } = useSurgeryCenters(editQueryOptions);

  const practices = practicesRaw
    .filter((p): p is typeof p & { value: string } => !!p.value)
    .map((p) => ({ value: p.value, label: p.label }));

  const surgeryCenters = surgeryCentersRaw
    .filter((s): s is typeof s & { value: string } => !!s.value)
    .map((s) => ({ value: s.value, label: s.label }));

  const physicians = physiciansRaw
    .filter((p): p is typeof p & { value: string } => !!p.value)
    .map((p) => ({ value: p.value, label: p.label }));

  const patientName = `${patient.lastName || ""}${
    patient.lastName && patient.firstName ? ", " : ""
  }${patient.firstName || ""}`;

  const savePatient = useCallback(
    (fields: Record<string, any>) => {
      if (!caseId) return;
      updateRequisition.mutate(
        { caseId, data: { patient: fields } },
        { onSuccess: () => onSaved?.() }
      );
    },
    [caseId, updateRequisition, onSaved]
  );

  const saveCase = useCallback(
    (fields: Record<string, any>) => {
      if (!caseId) return;
      updateRequisition.mutate(
        { caseId, data: { case: fields } },
        { onSuccess: () => onSaved?.() }
      );
    },
    [caseId, updateRequisition, onSaved]
  );

  const [practiceEditing, setPracticeEditing] = useState(false);
  const [practiceNameOverride, setPracticeNameOverride] = useState<
    string | null
  >(null);

  const effectiveIdentity = resolveEffectiveClientReportIdentity(
    {
      caseNumber: caseInfo.caseNumber,
      facility: {
        name: facility.name,
        medicalDirector: null,
        address: facility.address,
        phone: facility.phone,
        fax: facility.fax,
        logoUrl: facilityLogoUrl || null,
      },
      practice: {
        ...practice,
        logoUrl: practiceLogoUrl || practice.logoUrl || null,
        footerHtml: practice.footerHtml || null,
      },
    },
    reportIdentity
  );
  const reportHeader = effectiveIdentity.header;
  const isPracticeIdentity = effectiveIdentity.source === "sending_practice";
  const reportHeaderPhoneInfo = [
    reportHeader.phone && `Phone ${reportHeader.phone}`,
    reportHeader.fax && `Fax ${reportHeader.fax}`,
  ]
    .filter(Boolean)
    .join("  ");

  return (
    <Box style={{ marginBottom: 16 }}>
      {/* Practice / Facility header */}
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        {practiceEditing && !isReadOnly ? (
          <Box
            style={{
              minWidth: 300,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setPracticeEditing(false);
              }
            }}
          >
            <Select
              label="Practice"
              data={practices}
              value={caseInfo.sendingPracticeId ?? null}
              placeholder="Select practice"
              searchable
              autoFocus
              size="xs"
              onChange={(value) => {
                if (value) {
                  const selected = practices.find((p) => p.value === value);
                  if (selected) setPracticeNameOverride(selected.label);
                  saveCase({ sendingPracticeId: value });
                }
              }}
            />
            <Select
              label="Surgical Center"
              data={surgeryCenters}
              value={caseInfo.surgeryCenterLabId ?? null}
              placeholder="Select surgical center"
              searchable
              size="xs"
              onChange={(value) => {
                if (value) {
                  saveCase({ surgeryCenterLabId: value });
                }
              }}
            />
          </Box>
        ) : (
          <>
            {/* The report identity is resolved by prefix/account. A procedure
                site (surgery center) is never eligible for this lab block. */}
            <ReportLaboratoryLogo source={reportHeader.logoUrl} />

            <Box
              style={{
                textAlign: "right",
                cursor:
                  isReadOnly || !isPracticeIdentity ? "default" : "pointer",
              }}
              onClick={() =>
                !isReadOnly && isPracticeIdentity && setPracticeEditing(true)
              }
              className={
                isReadOnly || !isPracticeIdentity
                  ? undefined
                  : "header-editable-value"
              }
            >
              <Text style={headerPracticeNameStyle}>
                {isPracticeIdentity && practiceNameOverride
                  ? practiceNameOverride
                  : reportHeader.name}
              </Text>
              {reportHeader.medicalDirector && (
                <Text style={headerContactLineStyle}>
                  {reportHeader.medicalDirector}, Medical Director
                </Text>
              )}
              {reportHeader.address && (
                <Text style={headerContactLineStyle}>
                  {reportHeader.address}
                </Text>
              )}
              {reportHeaderPhoneInfo && (
                <Text style={headerContactLineStyle}>
                  {reportHeaderPhoneInfo}
                </Text>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Report title — gray fill bar with thin black border, full content width.
          Margins measured from sample PDF: 26pt above (contact block bottom → bar top),
          8pt below (bar bottom → PATIENT line top). */}
      {!isContinuationPage && (
        <>
          <Box
            style={{
              ...reportTitleBarStyle,
              marginTop: "8pt",
              marginBottom: "8pt",
            }}
          >
            <Text style={reportTitleTextStyle}>{title}</Text>
          </Box>

          {/* Patient / case info — two-column grid.
          Left col: PATIENT (12pt Bold) / DOB+AGE+SEX inline (10pt) / MRN / PHYSICIAN / COPY TO.
          Right col: CASE NUMBER (12pt Bold) / DATE COLLECTED / DATE RECEIVED / DATE REPORTED — values flush right.
          No bottom border (per PDF/screenshot). */}
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr",
              columnGap: 24,
            }}
          >
            {/* Left column */}
            <Box>
              {/* Row 1 — 12pt Bold */}
              <EditableInfoRow
                label="PATIENT"
                displayValue={patientName}
                variant="row1"
                isReadOnly={isReadOnly}
                editor={
                  <PatientNameEditor
                    patient={patient}
                    savePatient={savePatient}
                  />
                }
              />

              {/* Row 2 — DATE OF BIRTH | AGE | SEX, inline (10pt) */}
              <Box
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 24,
                  rowGap: 0,
                  alignItems: "baseline",
                }}
              >
                <EditableInfoRow
                  label="DATE OF BIRTH"
                  displayValue={formatDate(patient.dateOfBirth)}
                  isReadOnly={isReadOnly}
                  editor={
                    <DateEditor
                      defaultValue={patient.dateOfBirth}
                      onSave={(date) => savePatient({ dateOfBirth: date })}
                    />
                  }
                />
                <InlineFieldPair label="AGE">
                  <Text style={patientRowStyle}>
                    {patient.age != null ? `${patient.age} y.o.` : "—"}
                  </Text>
                </InlineFieldPair>
                <EditableInfoRow
                  label="SEX"
                  displayValue={patient.sex || "—"}
                  minWidth={0}
                  isReadOnly={isReadOnly}
                  editor={
                    <InlineSelect
                      defaultValue={patient.sex ?? null}
                      data={[
                        { value: "M", label: "Male" },
                        { value: "F", label: "Female" },
                        { value: "Other", label: "Other" },
                      ]}
                      style={{ width: 140 }}
                      onChange={(value) => {
                        if (value) savePatient({ sex: value });
                      }}
                    />
                  }
                />
              </Box>

              {/* Row 3 */}
              <EditableInfoRow
                label="MRN"
                displayValue={patient.medicalRecordNumber || "—"}
                isReadOnly={isReadOnly}
                editor={
                  <MrnEditor
                    mrn={patient.medicalRecordNumber || ""}
                    savePatient={savePatient}
                  />
                }
              />

              {/* Row 4 */}
              <EditableInfoRow
                label="PHYSICIAN"
                displayValue={caseInfo.physicianName || "—"}
                isReadOnly={isReadOnly}
                editor={
                  <InlineSelect
                    defaultValue={caseInfo.physicianId ?? null}
                    data={physicians}
                    placeholder="Select physician"
                    searchable
                    style={{ width: 200 }}
                    onChange={(value) => {
                      if (value) saveCase({ physicianId: value });
                    }}
                  />
                }
              />

              {/* Row 5 */}
              <EditableInfoRow
                label="COPY TO"
                displayValue={caseInfo.copyTo || ""}
                isReadOnly={isReadOnly}
                editor={
                  <InlineSelect
                    defaultValue={
                      physicians.find((p) => p.label === caseInfo.copyTo)
                        ?.value ?? null
                    }
                    data={physicians}
                    placeholder="Select physician"
                    searchable
                    style={{ width: 200 }}
                    onChange={(_, label) => {
                      if (label) saveCase({ copyTo: label });
                    }}
                  />
                }
              />
            </Box>

            {/* Right column — values right-aligned via space-between */}
            <Box>
              {/* Row 1 — 12pt Bold */}
              <InfoRow
                label="CASE NUMBER"
                value={caseInfo.caseNumber}
                variant="row1"
                align="right"
              />

              {/* Row 2 — DATE COLLECTED (paired with DOB row on the left) */}
              <EditableInfoRow
                label="DATE COLLECTED"
                displayValue={formatDate(caseInfo.dateCollected)}
                align="right"
                isReadOnly={isReadOnly}
                editor={
                  <DateEditor
                    defaultValue={caseInfo.dateCollected}
                    onSave={(date) => saveCase({ dateCollected: date })}
                    width={140}
                  />
                }
              />

              {/* Row 3 — DATE RECEIVED (paired with MRN) */}
              <EditableInfoRow
                label="DATE RECEIVED"
                displayValue={formatDate(caseInfo.dateReceived)}
                align="right"
                isReadOnly={isReadOnly}
                editor={
                  <DateEditor
                    defaultValue={caseInfo.dateReceived}
                    onSave={(date) => saveCase({ dateReceived: date })}
                    width={140}
                  />
                }
              />

              {/* Row 4 — DATE REPORTED (paired with PHYSICIAN) */}
              <InfoRow
                label="DATE REPORTED"
                value={formatDate(caseInfo.dateReported)}
                align="right"
              />
            </Box>
          </Box>
        </>
      )}

      <style>{`
        .header-editable-value:hover {
          background: rgba(0, 100, 255, 0.04);
          outline: 1px dashed var(--mantine-color-gray-4);
        }
      `}</style>
    </Box>
  );
}
